import { useState, useMemo } from "react";
import {
  BarChart3,
  Target,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  Activity,
  PieChart as PieChartIcon,
  Search,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from "recharts";
import { PageHeader } from "@/components/common";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTran } from "@/hooks";

// ---------------------------------------------------------------------------
// Dummy data
// ---------------------------------------------------------------------------

const DUMMY_KPIS = {
  totalDisclosures: 48,
  completionRate: 72,
  onTarget: 31,
  needsAttention: 12,
};

const DUMMY_CATEGORY_PROGRESS = [
  { category: "Environmental", met: 13, inProgress: 4, below: 3, total: 20 },
  { category: "Social", met: 12, inProgress: 2, below: 1, total: 15 },
  { category: "Governance", met: 6, inProgress: 4, below: 3, total: 13 },
];

const DUMMY_CATEGORY_BREAKDOWN = [
  { name: "Environmental", value: 20, fill: "#10b981" },
  { name: "Social", value: 15, fill: "#3b82f6" },
  { name: "Governance", value: 13, fill: "#8b5cf6" },
];

const DUMMY_ORG_PERFORMANCE = [
  {
    name: "Connectivity Solutions",
    completion: 92,
    onTarget: 88,
    fill: "#10b981",
  },
  { name: "JJ Cambodia", completion: 85, onTarget: 76, fill: "#3b82f6" },
  { name: "JJ-Lapp Malaysia", completion: 78, onTarget: 70, fill: "#8b5cf6" },
  { name: "Delano Center", completion: 65, onTarget: 58, fill: "#f59e0b" },
  { name: "Bayan Lepas", completion: 52, onTarget: 40, fill: "#ef4444" },
];

const DUMMY_DISCLOSURES = [
  {
    code: "EN001",
    title: "Total Energy Consumption",
    category: "Environmental",
    framework: "JJ-Group",
    orgCount: 12,
    enteredOrgs: 11,
    achievement: 92,
    unit: "MWh",
    trend: "up",
  },
  {
    code: "EN002",
    title: "Total Energy Imported/Purchased",
    category: "Environmental",
    framework: "JJ-Group",
    orgCount: 12,
    enteredOrgs: 8,
    achievement: 78,
    unit: "MWh",
    trend: "up",
  },
  {
    code: "GHG001",
    title: "Total Scope 1 GHG Emissions",
    category: "Environmental",
    framework: "JJ-Group",
    orgCount: 12,
    enteredOrgs: 10,
    achievement: 80,
    unit: "tCO₂e",
    trend: "down",
  },
  {
    code: "GHG002",
    title: "Total Scope 2 GHG Emissions",
    category: "Environmental",
    framework: "JJ-Group",
    orgCount: 12,
    enteredOrgs: 5,
    achievement: 40,
    unit: "tCO₂e",
    trend: "down",
  },
  {
    code: "WAT001",
    title: "Total Water Consumption",
    category: "Environmental",
    framework: "JJ-Group",
    orgCount: 12,
    enteredOrgs: 9,
    achievement: 105,
    unit: "m³",
    trend: "down",
  },
  {
    code: "WST001",
    title: "Total Waste Generated",
    category: "Environmental",
    framework: "JJ-Group",
    orgCount: 12,
    enteredOrgs: 7,
    achievement: 68,
    unit: "tonnes",
    trend: "up",
  },
  {
    code: "SOC001",
    title: "Employee Turnover Rate",
    category: "Social",
    framework: "JJ-Group",
    orgCount: 12,
    enteredOrgs: 12,
    achievement: 100,
    unit: "%",
    trend: "flat",
  },
  {
    code: "SOC003",
    title: "Training Hours per Employee",
    category: "Social",
    framework: "JJ-Group",
    orgCount: 12,
    enteredOrgs: 9,
    achievement: 88,
    unit: "hours",
    trend: "up",
  },
  {
    code: "SOC010",
    title: "Workplace Injury Rate",
    category: "Social",
    framework: "JJ-Group",
    orgCount: 12,
    enteredOrgs: 12,
    achievement: 55,
    unit: "per 1M hrs",
    trend: "down",
  },
  {
    code: "GOV001",
    title: "Board Independence",
    category: "Governance",
    framework: "JJ-Group",
    orgCount: 12,
    enteredOrgs: 0,
    achievement: null,
    unit: "%",
    trend: null,
  },
  {
    code: "SGX 101-1",
    title: "Absolute Emissions",
    category: "Environmental",
    framework: "SGX",
    orgCount: 8,
    enteredOrgs: 8,
    achievement: 83,
    unit: "Mt CO₂e",
    trend: "down",
  },
  {
    code: "SGX 102-1",
    title: "Total Energy Consumption",
    category: "Environmental",
    framework: "SGX",
    orgCount: 8,
    enteredOrgs: 6,
    achievement: 95,
    unit: "MJ",
    trend: "up",
  },
];

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

const TOOLTIP_STYLE = {
  backgroundColor: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 8,
  fontSize: 12,
  color: "hsl(var(--foreground))",
};

const CATEGORY_BADGE = {
  Environmental: "bg-emerald-500/15 text-emerald-500",
  Social: "bg-blue-500/15 text-blue-500",
  Governance: "bg-purple-500/15 text-purple-500",
};

const STATUS_COLORS = {
  met: "#10b981",
  inProgress: "#f59e0b",
  below: "#ef4444",
};

// ---------------------------------------------------------------------------
// KPI Cards
// ---------------------------------------------------------------------------

const KPI_CONFIG = [
  {
    key: "totalDisclosures",
    label: "Total Disclosures",
    sub: "quantitative metrics assigned",
    icon: BarChart3,
    color: "text-blue-500",
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
  },
  {
    key: "completionRate",
    label: "Data Completion",
    sub: "disclosures with entries",
    icon: Activity,
    color: "text-amber-500",
    bg: "bg-amber-500/10",
    border: "border-amber-500/20",
    suffix: "%",
  },
  {
    key: "onTarget",
    label: "On Target",
    sub: "meeting or exceeding goals",
    icon: CheckCircle,
    color: "text-emerald-500",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/20",
    format: (v) => `${v} / ${DUMMY_KPIS.totalDisclosures}`,
  },
  {
    key: "needsAttention",
    label: "Needs Attention",
    sub: "below target threshold",
    icon: AlertTriangle,
    color: "text-red-500",
    bg: "bg-red-500/10",
    border: "border-red-500/20",
  },
];

function KpiCards() {
  const { t } = useTran();

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {KPI_CONFIG.map((kpi) => {
        const Icon = kpi.icon;
        const raw = DUMMY_KPIS[kpi.key];
        const display = kpi.format
          ? kpi.format(raw)
          : `${raw}${kpi.suffix || ""}`;
        return (
          <Card key={kpi.key} className={`border ${kpi.border}`}>
            <CardContent className="flex items-center gap-4 pt-2">
              <div className={`rounded-xl p-3 ${kpi.bg}`}>
                <Icon size={24} className={kpi.color} strokeWidth={1.8} />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold tracking-tight">{display}</p>
                <p className="text-xs font-medium text-muted-foreground">
                  {t(kpi.label)}
                </p>
                <p className="truncate text-[11px] text-muted-foreground/60">
                  {t(kpi.sub)}
                </p>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Category Breakdown (Donut)
// ---------------------------------------------------------------------------

function CategoryDonut() {
  const { t } = useTran();
  const chartData = useMemo(
    () =>
      DUMMY_CATEGORY_BREAKDOWN.map((entry) => ({
        ...entry,
        name: t(entry.name),
      })),
    [t],
  );
  const total = chartData.reduce((s, c) => s + c.value, 0);
  return (
    <Card className="col-span-2">
      <CardHeader className="border-b border-border">
        <CardTitle className="text-sm font-medium">
          {t("Category Breakdown")}
        </CardTitle>
        <CardDescription>
          {t("Disclosure distribution by ESG pillar")}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-center pt-4">
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={90}
              paddingAngle={3}
              dataKey="value"
              stroke="none"
            >
              {chartData.map((entry, i) => (
                <Cell key={i} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(v) => [t("{{count}} disclosures", { count: v })]}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex gap-4 text-xs">
          {chartData.map((c) => (
            <div key={c.name} className="flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: c.fill }}
              />
              <span className="text-muted-foreground">{c.name}</span>
              <span className="font-semibold">
                {Math.round((c.value / total) * 100)}%
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Category Progress (Horizontal Stacked)
// ---------------------------------------------------------------------------

function CategoryProgressChart() {
  const { t } = useTran();
  const data = useMemo(
    () =>
      DUMMY_CATEGORY_PROGRESS.map((c) => ({
        ...c,
        category: t(c.category),
        metPct: Math.round((c.met / c.total) * 100),
        inProgressPct: Math.round((c.inProgress / c.total) * 100),
        belowPct: Math.round((c.below / c.total) * 100),
      })),
    [t],
  );
  return (
    <Card className="col-span-3 lg:col-span-3">
      <CardHeader className="border-b border-border">
        <CardTitle className="text-sm font-medium">
          {t("Target Progress by Category")}
        </CardTitle>
        <CardDescription>
          {t(
            "Proportion of disclosures meeting, approaching, or below targets",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={data} layout="vertical" barSize={28}>
            <CartesianGrid
              strokeDasharray="3 3"
              horizontal={false}
              stroke="hsl(var(--border))"
            />
            <XAxis
              type="number"
              domain={[0, 100]}
              tickFormatter={(v) => `${v}%`}
              tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
            />
            <YAxis
              type="category"
              dataKey="category"
              width={100}
              tick={{ fontSize: 12, fill: "hsl(var(--foreground))" }}
            />
            <Tooltip
              formatter={(v, name) => [`${v}%`, name]}
              contentStyle={TOOLTIP_STYLE}
            />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 12 }}
            />
            <Bar
              dataKey="metPct"
              name={t("Met / Exceeded")}
              stackId="a"
              fill={STATUS_COLORS.met}
            />
            <Bar
              dataKey="inProgressPct"
              name={t("In Progress")}
              stackId="a"
              fill={STATUS_COLORS.inProgress}
            />
            <Bar
              dataKey="belowPct"
              name={t("Below Target")}
              stackId="a"
              fill={STATUS_COLORS.below}
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Top / Bottom Orgs
// ---------------------------------------------------------------------------

function OrgPerformanceCard() {
  const { t } = useTran();

  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle className="text-sm font-medium">
          {t("Organization Performance")}
        </CardTitle>
        <CardDescription>
          {t("Top organizations by completion and on-target rate")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 pt-4">
        {DUMMY_ORG_PERFORMANCE.map((org) => (
          <div key={org.name} className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium">{org.name}</span>
              <span className="text-muted-foreground">
                {t("{{count}}% complete", { count: org.completion })}
              </span>
            </div>
            <div className="flex h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="rounded-full transition-all"
                style={{ width: `${org.onTarget}%`, backgroundColor: org.fill }}
              />
              <div
                className="transition-all"
                style={{
                  width: `${org.completion - org.onTarget}%`,
                  backgroundColor: org.fill,
                  opacity: 0.35,
                }}
              />
            </div>
            <div className="flex gap-3 text-[11px] text-muted-foreground">
              <span>
                {t("On Target")}:{" "}
                <span className="font-medium text-foreground">
                  {org.onTarget}%
                </span>
              </span>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Disclosure Detail Table
// ---------------------------------------------------------------------------

function getStatus(d, t) {
  if (d.avgActual == null)
    return {
      label: t("No data"),
      color: "text-muted-foreground",
      dot: "bg-muted-foreground",
    };
  if (d.avgTarget == null)
    return {
      label: t("No target"),
      color: "text-muted-foreground",
      dot: "bg-muted-foreground",
    };
  const pct = Math.round((d.avgActual / d.avgTarget) * 100);
  if (pct >= 100)
    return {
      label: `${pct}%`,
      color: "text-emerald-500",
      dot: "bg-emerald-500",
    };
  if (pct >= 70)
    return { label: `${pct}%`, color: "text-amber-500", dot: "bg-amber-500" };
  return { label: `${pct}%`, color: "text-red-500", dot: "bg-red-500" };
}

const TrendIcon = ({ trend }) => {
  if (trend === "up")
    return <TrendingUp size={14} className="text-emerald-500" />;
  if (trend === "down")
    return <TrendingDown size={14} className="text-red-500" />;
  if (trend === "flat")
    return <Minus size={14} className="text-muted-foreground" />;
  return null;
};

function DisclosureTable({ search, framework }) {
  const { t } = useTran();
  const filtered = useMemo(() => {
    let list = DUMMY_DISCLOSURES;
    if (framework && framework !== "all")
      list = list.filter((d) => d.framework === framework);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (d) =>
          d.code.toLowerCase().includes(q) || d.title.toLowerCase().includes(q),
      );
    }
    return list;
  }, [search, framework]);

  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle className="text-sm font-medium">
          {t("Disclosure Detail")}
        </CardTitle>
        <CardDescription>
          {t("Per-disclosure progress across all organizations")}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[110px]">{t("Code")}</TableHead>
              <TableHead>{t("Title")}</TableHead>
              <TableHead className="w-[110px]">{t("Category")}</TableHead>
              <TableHead className="w-[70px] text-center">
                {t("Orgs")}
              </TableHead>
              <TableHead className="w-[100px] text-right">
                {t("Target")}
              </TableHead>
              <TableHead className="w-[100px] text-right">
                {t("Actual")}
              </TableHead>
              <TableHead className="w-[80px] text-center">
                {t("Status")}
              </TableHead>
              <TableHead className="w-[50px] text-center">
                {t("Trend")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((d) => {
              const status = getStatus(d, t);
              return (
                <TableRow key={d.code} className="group hover:bg-muted/50">
                  <TableCell className="font-mono text-xs font-medium">
                    {d.code}
                  </TableCell>
                  <TableCell className="text-sm">{t(d.title)}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`text-[11px] ${CATEGORY_BADGE[d.category]}`}
                    >
                      {t(d.category)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center text-sm">
                    <span className="font-medium">{d.enteredOrgs}</span>
                    <span className="text-muted-foreground">/{d.orgCount}</span>
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {d.avgTarget != null ? (
                      <>
                        {d.avgTarget.toLocaleString()}{" "}
                        <span className="text-xs text-muted-foreground">
                          {d.unit}
                        </span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">&mdash;</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {d.avgActual != null ? (
                      <>
                        {d.avgActual.toLocaleString()}{" "}
                        <span className="text-xs text-muted-foreground">
                          {d.unit}
                        </span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">&mdash;</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      <span
                        className={`inline-block h-2 w-2 rounded-full ${status.dot}`}
                      />
                      <span className={`text-xs font-medium ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex justify-center">
                      <TrendIcon trend={d.trend} />
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="py-8 text-center text-sm text-muted-foreground"
                >
                  {t("No disclosures found")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function KpiDashboardPage() {
  const { t } = useTran();
  const [search, setSearch] = useState("");
  const [framework, setFramework] = useState("all");

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("ESG Dashboard")}
        description={t(
          "Track data entry completion and target progress across disclosures and organizations.",
        )}
        icon={BarChart3}
        actions={
          <div className="flex items-center gap-2">
            <Select value={framework} onValueChange={setFramework}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder={t("Framework")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("All Frameworks")}</SelectItem>
                <SelectItem value="SGX">SGX</SelectItem>
                <SelectItem value={"JJ-Group"}>{"JJ-Group"}</SelectItem>
                <SelectItem value="GRI">GRI</SelectItem>
              </SelectContent>
            </Select>
            <Select defaultValue="2025">
              <SelectTrigger className="w-[100px]">
                <SelectValue placeholder={t("Year")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2025">2025</SelectItem>
                <SelectItem value="2024">2024</SelectItem>
                <SelectItem value="2023">2023</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      />

      {/* Row 1: KPI Cards */}
      <KpiCards />

      {/* Row 2: Category Progress + Category Donut */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <CategoryProgressChart />
        <CategoryDonut />
      </div>

      {/* Row 3: Org Performance */}
      <OrgPerformanceCard />

      {/* Row 5: Disclosure Detail Table */}
      <div className="relative w-64">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          placeholder={t("Search disclosures...")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>
      <DisclosureTable search={search} framework={framework} />
    </div>
  );
}
